import { cn } from '@/lib/utils';
import type { OddsMove } from '@tennis-scanner/shared';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { useEffect, useState } from 'react';

// Header-bar chips for the trading chart: move chip (drop/rise + favorability)
// and freshness overlay (tick age with traffic-light color). Extracted from
// `odds-trading-chart.tsx` to keep that file under the 300-line cap.

// Favorability matrix (operator perspective):
//   pick + drop  → favorable (green) — market AGREES with the pick
//   pick + rise  → unfavorable (red) — market DISAGREES with the pick
//   rival + drop → unfavorable (red) — market favors rival
//   rival + rise → favorable (green) — market dislikes rival
export function MoveChip({ move, side }: { move: OddsMove; side: 'pick' | 'rival' }) {
  const isFavorable = (side === 'pick') === (move.direction === 'drop');
  const cls = isFavorable
    ? 'border-positive/40 bg-positive/10 text-positive'
    : 'border-destructive/40 bg-destructive/10 text-destructive';
  const label = side === 'pick' ? 'tu pick' : 'rival';
  const Arrow = move.direction === 'drop' ? TrendingDown : TrendingUp;
  const dirLabel = move.direction === 'drop' ? 'baja' : 'sube';
  // Show recovery percentage only when meaningful (≥10%) — otherwise it
  // adds noise. The detector already filters ≥50% recovery, so values
  // here are always 0..50%.
  const reversionPct = Math.round(move.reversion * 100);
  const tooltip = [
    `${label} ${dirLabel} ${Math.abs(move.percentChange).toFixed(1)}% en ${move.book}`,
    `desde ${new Date(move.since).toLocaleTimeString()}`,
    reversionPct >= 10 ? `recuperado ${reversionPct}%` : null,
    isFavorable ? 'favorable' : 'desfavorable',
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border px-1.5 py-0 font-mono text-[11px] font-semibold',
        cls,
      )}
      title={tooltip}
    >
      <Arrow className="size-3" />
      {label} {move.percentChange > 0 ? '+' : ''}
      {move.percentChange.toFixed(1)}%
    </span>
  );
}

// Freshness overlay — small badge top-right of the chart showing the age
// of the most recent tick with traffic-light color. Operator must know
// if data is stale BEFORE acting on what they see.
//
// Thresholds (arch § 9.6 derived):
//   ≤ 10s green  — connector emitting at expected ~10s cadence
//   ≤ 30s yellow — small lag, possibly recoverable
//   >  30s red   — significant staleness, investigate
export function FreshnessOverlay({ latestObservedAt }: { latestObservedAt: string | null }) {
  // 1s tick keeps the displayed age in sync with the client clock without
  // requiring a re-fetch. The actual data still polls every POLL_MS via
  // useQuery — this is just the display.
  const [tickMs, setTickMs] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setTickMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  if (!latestObservedAt) return null;
  const observedMs = Date.parse(latestObservedAt);
  if (!Number.isFinite(observedMs)) return null;
  const ageSec = Math.max(0, Math.floor((tickMs - observedMs) / 1000));

  const tone =
    ageSec <= 10
      ? 'border-positive/50 bg-positive/15 text-positive'
      : ageSec <= 30
        ? 'border-warning/50 bg-warning/15 text-warning'
        : 'border-destructive/50 bg-destructive/15 text-destructive';
  const dotTone = ageSec <= 10 ? 'bg-positive' : ageSec <= 30 ? 'bg-warning' : 'bg-destructive';

  return (
    <div
      className={cn(
        'pointer-events-none absolute right-2 top-2 z-10 flex items-center gap-1.5 rounded-md border px-2 py-1 font-mono text-[11px] font-semibold backdrop-blur-sm',
        tone,
      )}
      title={`Última cuota recibida ${formatAge(ageSec)} (${new Date(observedMs).toLocaleTimeString()})`}
    >
      <span className={cn('size-1.5 rounded-full', dotTone)} />
      <span>última cuota hace {formatAge(ageSec)}</span>
    </div>
  );
}

function formatAge(sec: number): string {
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  return `${Math.floor(sec / 3600)}h`;
}
